import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus, Search, Copy, Check, Tag, Percent,
    RefreshCw, Edit2, Trash2, ToggleLeft, ToggleRight, Lock, X, Package,
    Users, Calendar, Clock, Shield, Info, ArrowRight, Zap
} from 'lucide-react';
import { useCustomers } from '../../context/CustomerContext';
import { promotionsService } from '../../services/promotionService';
import ProductSelector from './promotions/components/ProductSelector';
import FilterDropdown from './promotions/components/FilterDropdown';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { supabase } from '../../supabase/config';
import { logActivity } from '../../utils/activityLogger';
import PremiumDatePicker from '../../components/PremiumDatePicker';
import { defaultLoyaltyLevels, defaultVisitRoles } from '../../utils/loyalty';

const CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const generateCode = () => {
    let code = '';
    for (let i = 0; i < 7; i++) {
        code += CHARS[Math.floor(Math.random() * CHARS.length)];
    }
    return code;
};

const initialForm = {
    title: '',
    code: '',
    type: 'percentage',
    value: '',
    minAmount: '',
    totalLimit: '',
    customerLimit: '',
    expiryDate: null,
    startDate: new Date(),
    startTime: '00:00',
    expiryTime: '23:59',
    isActive: true,
    target: 'all',
    loyaltyStatus: [],
    visitRole: [],
    selectedCustomers: [],
    productScope: 'all',
    selectedProducts: [],
    applicableDays: [0, 1, 2, 3, 4, 5, 6],
    campaignType: 'simple',
    buyItems: [],
    getItems: [],
    buyQuantity: '1',
    getQuantity: '1',
    bundleDiscountValue: '100',
    sameItemMode: true,
};

const Coupons = ({ hideHeader = false }) => {
    const { t } = useTranslation();
    const { effectiveTenantId, user, userData, tenantData, hasPermission, permissionsLoading } = useAuth();
    const { showConfirm } = useAlert();

    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [menuData, setMenuData] = useState(null);
    const { customers } = useCustomers();

    const fetchCoupons = useCallback(async () => {
        if (!effectiveTenantId) return;
        setLoading(true);
        const { data, error } = await supabase
            .from('discounts')
            .select('*')
            .eq('tenant_id', effectiveTenantId)
            .in('campaign_type', ['coupon', 'coupon_bundle'])
            .order('created_at', { ascending: false });
        
        if (error) {
            console.error("Kuponlar çekilirken hata:", error);
            setLoading(false);
            return;
        }

        setCoupons((data || []).map(row => ({
            id: row.id,
            title: row.title,
            code: row.description || '',
            type: row.type || 'percentage',
            value: row.value,
            minAmount: row.min_amount,
            totalLimit: row.total_limit,
            usageCount: row.usage_count || 0,
            expiryDate: row.expiry_date ? row.expiry_date.split('T')[0] : null,
            startDate: row.start_date || new Date(),
            startTime: row.start_time || '00:00',
            expiryTime: row.expiry_time || '23:59',
            customerLimit: row.customer_limit,
            target: row.target || 'all',
            loyaltyStatus: row.loyalty_status || [],
            visitRole: row.visit_role || [],
            selectedCustomers: row.selected_customers || [],
            productScope: row.product_scope || 'all',
            selectedProducts: row.selected_products || [],
            applicableDays: row.applicable_days || [0, 1, 2, 3, 4, 5, 6],
            campaignType: row.campaign_type === 'coupon_bundle' ? 'bundle' : 'simple',
            buyItems: row.buy_items || [],
            getItems: row.get_items || [],
            buyQuantity: row.buy_quantity || '1',
            getQuantity: row.get_quantity || '1',
            bundleDiscountValue: row.bundle_discount_value || '100',
            sameItemMode: row.same_item_mode !== false,
            isActive: row.is_active,
            createdAt: row.created_at,
        })));
        setLoading(false);
    }, [effectiveTenantId]);

    useEffect(() => { 
        fetchCoupons(); 
        if (effectiveTenantId) {
            promotionsService.fetchMenu(effectiveTenantId).then(data => {
                if (data) setMenuData(data);
            });
        }
    }, [fetchCoupons, effectiveTenantId]);

    useEffect(() => {
        if (!effectiveTenantId) return;
        const channel = supabase.channel('coupons-' + effectiveTenantId)
            .on('postgres_changes', {
                event: '*', schema: 'public', table: 'discounts',
                filter: `tenant_id=eq.${effectiveTenantId}`
            }, fetchCoupons)
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [effectiveTenantId, fetchCoupons]);

    const openCreate = () => {
        setEditingItem(null);
        setForm({ ...initialForm, code: generateCode() });
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setEditingItem(item);
        setForm({
            title: item.title || '',
            code: item.code || '',
            type: item.type || 'percentage',
            value: String(item.value ?? ''),
            minAmount: String(item.minAmount ?? ''),
            totalLimit: String(item.totalLimit ?? ''),
            customerLimit: String(item.customerLimit ?? ''),
            expiryDate: item.expiryDate || null,
            startDate: item.startDate || new Date(),
            startTime: item.startTime || '00:00',
            expiryTime: item.expiryTime || '23:59',
            target: item.target || 'all',
            loyaltyStatus: item.loyaltyStatus || [],
            visitRole: item.visitRole || [],
            selectedCustomers: item.selectedCustomers || [],
            productScope: item.productScope || 'all',
            selectedProducts: item.selectedProducts || [],
            applicableDays: item.applicableDays || [0, 1, 2, 3, 4, 5, 6],
            campaignType: item.campaignType || 'simple',
            buyItems: item.buyItems || [],
            getItems: item.getItems || [],
            buyQuantity: String(item.buyQuantity || '1'),
            getQuantity: String(item.getQuantity || '1'),
            bundleDiscountValue: String(item.bundleDiscountValue || '100'),
            sameItemMode: item.sameItemMode !== false,
            isActive: item.isActive !== false,
        });
        setModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.code.trim() || !form.value) return;
        setSaving(true);
        try {
            const row = {
                tenant_id: effectiveTenantId,
                title: form.title.trim(),
                description: form.code.trim().toUpperCase(),
                type: form.type,
                value: form.campaignType === 'bundle' ? 0 : (parseFloat(form.value) || 0),
                min_amount: parseFloat(form.minAmount) || null,
                total_limit: parseInt(form.totalLimit) || null,
                customer_limit: parseInt(form.customerLimit) || null,
                expiry_date: form.expiryDate || null,
                start_date: form.startDate,
                start_time: form.startTime || '00:00',
                expiry_time: form.expiryTime || '23:59',
                target: form.target,
                loyalty_status: form.loyaltyStatus || [],
                visit_role: form.visitRole || [],
                selected_customers: form.selectedCustomers || [],
                product_scope: form.productScope,
                selected_products: form.selectedProducts || [],
                applicable_days: form.applicableDays || [0, 1, 2, 3, 4, 5, 6],
                campaign_type: form.campaignType === 'bundle' ? 'coupon_bundle' : 'coupon',
                buy_items: form.buyItems || [],
                get_items: form.getItems || [],
                buy_quantity: Number(form.buyQuantity) || 1,
                get_quantity: Number(form.getQuantity) || 1,
                bundle_discount_value: Number(form.bundleDiscountValue) || 100,
                same_item_mode: form.sameItemMode !== false,
                is_active: form.isActive
            };
            if (editingItem) {
                await supabase.from('discounts').update(row).eq('id', editingItem.id);
                await logActivity(effectiveTenantId, userData?.email || user?.email, user?.id, 'UPDATE_COUPON', `Kupon güncellendi: ${form.code}`);
            } else {
                await supabase.from('discounts').insert(row);
                await logActivity(effectiveTenantId, userData?.email || user?.email, user?.id, 'CREATE_COUPON', `Kupon oluşturuldu: ${form.code}`);
            }
            setModalOpen(false);
            fetchCoupons();
        } catch (err) {
            console.error('Kupon kaydetme hatası:', err);
        }
        setSaving(false);
    };

    const handleDelete = async (item) => {
        if (!await showConfirm(`"${item.title}" kuponunu silmek istediğinize emin misiniz?`, { title: 'Kuponu Sil', confirmText: 'Sil', type: 'warning' })) return;
        await supabase.from('discounts').delete().eq('id', item.id);
        await logActivity(effectiveTenantId, userData?.email || user?.email, user?.id, 'DELETE_COUPON', `Kupon silindi: ${item.code}`);
        fetchCoupons();
    };

    const handleToggle = async (item) => {
        await supabase.from('discounts').update({ is_active: !item.isActive }).eq('id', item.id);
        fetchCoupons();
    };

    const copyCode = (id, code) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    if (permissionsLoading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
            <div style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
    );

    if (!userData?.isSuperAdmin && !hasPermission('discounts', 'view')) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
            <Lock size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem', opacity: 0.5 }} />
            <h3>{t('common.accessDenied')}</h3>
            <p style={{ color: 'var(--text-muted)' }}>{t('common.noPermission')}</p>
        </div>
    );

    const canCreate = userData?.isSuperAdmin || hasPermission('discounts', 'create');
    const canEdit = userData?.isSuperAdmin || hasPermission('discounts', 'edit');
    const canDelete = userData?.isSuperAdmin || hasPermission('discounts', 'delete');

    const [filters, setFilters] = useState({ status: 'all', type: 'all', sortBy: 'newest' });

    const filtered = coupons.filter(c => {
        const searchMatch = !search ||
            c.title?.toLowerCase().includes(search.toLowerCase()) ||
            c.code?.toLowerCase().includes(search.toLowerCase());
        const statusMatch = filters.status === 'all' || 
            (filters.status === 'active' && c.isActive !== false) || 
            (filters.status === 'passive' && c.isActive === false);
        const typeMatch = filters.type === 'all' || c.type === filters.type;
        return searchMatch && statusMatch && typeMatch;
    }).sort((a, b) => {
        if (filters.sortBy === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
        if (filters.sortBy === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
        if (filters.sortBy === 'title') return (a.title || '').localeCompare(b.title || '');
        return 0;
    });

    return (
        <div className="fade-in">
            {!hideHeader && (
                <header style={{ marginBottom: '2.5rem' }}>
                    <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>Kuponlar</h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Müşterilerinizin ödeme sırasında kullanabileceği indirim kupon kodları oluşturun.</p>
                </header>
            )}

            <div className="promotions-controls-row" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: '2.5rem' }}>
                <div className="search-container" style={{ position: 'relative', flex: 1, minWidth: '320px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Kuponlarda ara..."
                        style={{ paddingLeft: '40px', width: '100%', height: '46px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '12px', outline: 'none' }}
                    />
                </div>
                
                <div className="settings-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {canCreate && (
                        <button onClick={openCreate} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '46px', padding: '0 24px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                            <Plus size={20} />
                            <span className="full-text">Yeni Kupon Tanımla</span>
                            <span className="short-text">Kupon Ekle</span>
                        </button>
                    )}
                    
                    <FilterDropdown filters={filters} setFilters={setFilters} activeTab="coupons" />
                </div>
            </div>

            {loading ? (
                <div style={{ padding: '4rem', textAlign: 'center' }}><div className="animate-spin" style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto' }}></div></div>
            ) : filtered.length === 0 ? (
                <div className="card glass" style={{ padding: '5rem 2rem', textAlign: 'center' }}>
                    <div style={{ width: '80px', height: '80px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                        <Tag size={40} />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Henüz kupon yok</h3>
                    <p style={{ color: 'var(--text-muted)' }}>{canCreate ? 'İlk kuponunuzu oluşturmak için butona tıklayın.' : 'Henüz kupon oluşturulmamış.'}</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))', gap: '1.5rem' }}>
                    {filtered.map(coupon => (
                        <CouponCard
                            key={coupon.id}
                            coupon={coupon}
                            copied={copiedId === coupon.id}
                            onCopy={() => copyCode(coupon.id, coupon.code)}
                            onEdit={() => openEdit(coupon)}
                            onDelete={() => handleDelete(coupon)}
                            onToggle={() => handleToggle(coupon)}
                            canEdit={canEdit}
                            canDelete={canDelete}
                        />
                    ))}
                </div>
            )}

            {isModalOpen && (
                <CouponModal
                    form={form}
                    setForm={setForm}
                    onSave={handleSave}
                    onClose={() => setModalOpen(false)}
                    saving={saving}
                    isEdit={!!editingItem}
                    customers={customers || []}
                    menuData={menuData}
                    tenantData={tenantData}
                    t={t}
                />
            )}
        </div>
    );
};

const CouponCard = ({ coupon, copied, onCopy, onEdit, onDelete, onToggle, canEdit, canDelete }) => {
    const isExpired = coupon.expiryDate && new Date(coupon.expiryDate) < new Date();
    const isActive = coupon.isActive !== false;
    const iconColor = '#3b82f6';
    const isBundle = coupon.campaignType === 'bundle';

    return (
        <div className="card glass promotion-card-hover" style={{
            position: 'relative', transition: 'all 0.3s',
            opacity: isActive && !isExpired ? 1 : 0.55,
            filter: isActive && !isExpired ? 'none' : 'grayscale(100%)',
            overflow: 'hidden',
        }}>
            {/* Top accent line */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                background: iconColor, opacity: isActive ? 1 : 0.3,
            }} />

            {/* Header: Icon + Badge + Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', paddingTop: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                        background: iconColor,
                        color: 'white', padding: '10px', borderRadius: '12px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Tag size={20} />
                    </div>
                    <div style={{
                        padding: '3px 10px', borderRadius: '8px', fontSize: '0.7rem',
                        fontWeight: 700, background: iconColor + '20', color: iconColor,
                        letterSpacing: '0.02em',
                    }}>
                        {isBundle ? 'Kombin Kupon' : 'Kupon'}
                    </div>
                    {!isActive && (
                        <div style={{
                            padding: '3px 10px', borderRadius: '8px', fontSize: '0.65rem',
                            fontWeight: 700, background: 'rgba(0,0,0,0.06)', color: 'var(--text-muted)',
                        }}>
                            Pasif
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {canEdit && (
                        <>
                            <label className="switch" style={{ marginRight: '2px' }} title={isActive ? "Pasif Yap" : "Aktif Yap"}>
                                <input type="checkbox" checked={isActive} onChange={onToggle} />
                                <span className="slider"></span>
                            </label>
                            <button onClick={onEdit} style={{
                                background: 'rgba(var(--primary-rgb), 0.05)', color: 'var(--primary)',
                                padding: '7px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                transition: 'background 0.15s',
                            }} title="Düzenle">
                                <Edit2 size={16} />
                            </button>
                        </>
                    )}
                    {canDelete && (
                        <button onClick={onDelete} style={{
                            background: 'transparent', color: '#ef4444', border: 'none',
                            padding: '7px', cursor: 'pointer', borderRadius: '8px',
                            transition: 'background 0.15s',
                        }} title="Sil">
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            </div>

            {/* Title */}
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.5rem', lineHeight: 1.3 }}>
                {coupon.title}
            </h3>

            {/* Primary value */}
            <div style={{
                fontSize: '1.75rem', fontWeight: 800, color: iconColor,
                marginBottom: '1rem', display: 'flex', alignItems: 'baseline', gap: '8px',
            }}>
                {coupon.campaignType === 'bundle' ? (
                     <span style={{ fontSize: '1.2rem' }}>{coupon.buyQuantity} Al {coupon.getQuantity} {coupon.bundleDiscountValue == 100 ? 'Bedava' : `(%${coupon.bundleDiscountValue})`}</span>
                ) : (
                    <>
                        {coupon.type === 'percentage' ? `%${coupon.value}` : `${coupon.value} TL`}
                        <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-muted)' }}>İndirim</span>
                    </>
                )}
            </div>

            {/* Coupon code box */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(59, 130, 246, 0.04)', border: '1.5px dashed rgba(59, 130, 246, 0.25)', borderRadius: '12px', padding: '10px 14px', marginBottom: '1.25rem' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.1rem', letterSpacing: '0.05em', color: iconColor, flex: 1 }}>
                     {coupon.code}
                </span>
                <button
                    onClick={onCopy}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: '8px', border: `1px solid ${iconColor}40`, background: 'white', cursor: 'pointer', color: iconColor, fontSize: '0.7rem', fontWeight: 700, transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? 'Kopyalandı' : 'Kopyala'}
                </button>
            </div>

            {/* Info and Stats section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                    <div style={{ flex: 1, padding: '8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '2px' }}>Kullanım</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{coupon.usageCount || 0}<span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}> / {coupon.totalLimit || '∞'}</span></div>
                    </div>
                    <div style={{ flex: 1, padding: '8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '2px' }}>Min. Tutar</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{coupon.minAmount > 0 ? `${coupon.minAmount} TL` : '∞'}</div>
                    </div>
                    <div style={{ flex: 1.3, padding: '8px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '2px' }}>
                             Son Kullanma
                        </div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 700 }}>
                             {coupon.expiryDate ? (
                                <span style={{ color: isExpired ? '#ef4444' : 'inherit' }}>
                                    {new Date(coupon.expiryDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                                </span>
                            ) : (
                                <span style={{ color: 'var(--text-muted)' }}>Süresiz</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SectionHeader = ({ icon: Icon, title, subtitle, color }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
        <div style={{
            width: '36px', height: '36px', borderRadius: '10px',
            background: `${color || 'var(--primary)'}12`,
            color: color || 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
            <Icon size={18} />
        </div>
        <div>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-main)', margin: 0 }}>{title}</h3>
            {subtitle && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>{subtitle}</p>}
        </div>
    </div>
);

const SectionCard = ({ children, style }) => (
    <div style={{
        padding: '1.5rem', background: 'var(--bg-main)', borderRadius: '16px',
        border: '1px solid var(--border)', ...style
    }}>
        {children}
    </div>
);

const CouponModal = ({ form, setForm, onSave, onClose, saving, isEdit, customers, menuData, tenantData, t }) => {
    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const [isIndefinite, setIsIndefinite] = useState(false);
    const [customerSearchQuery, setCustomerSearchQuery] = useState('');
    const [productSearchQuery, setProductSearchQuery] = useState('');
    const [getSearchQuery, setGetSearchQuery] = useState('');

    useEffect(() => {
        setIsIndefinite(!form.expiryDate);
    }, [form.expiryDate]);

    const filteredCustomers = customers.filter(c => {
        if (!customerSearchQuery) return true;
        const query = customerSearchQuery.toLowerCase();
        return (c.name && c.name.toLowerCase().includes(query)) || (c.email && c.email.toLowerCase().includes(query)) || (c.phone && c.phone.includes(query));
    });

    const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
    const allDaysSelected = (form.applicableDays || []).length === 7;

    const getPreviewText = () => {
        if (form.campaignType === 'bundle') {
            const buyQ = form.buyQuantity || 1;
            const getQ = form.getQuantity || 1;
            const disc = form.bundleDiscountValue || 100;
            const buyNames = form.buyItems?.slice(0, 2).join(', ') || 'seçili ürünler';
            return `${buyQ} adet ${buyNames} al, ${getQ} adet ${disc === '100' || disc === 100 ? 'bedava' : `%${disc} indirimli`} kazan`;
        }
        if (!form.value) return '';
        if (form.type === 'percentage') return `Sepette %${form.value} indirim${form.minAmount ? ` (min. ${form.minAmount} TL)` : ''}`;
        if (form.type === 'fixed') return `Sepetten ${form.value} TL indirim${form.minAmount ? ` (min. ${form.minAmount} TL)` : ''}`;
        return '';
    };

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1.5rem' }}
            onClick={onClose}
        >
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(0, 0, 0, 0.1); border-radius: 20px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(0, 0, 0, 0.2); }
                input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
                input[type=number] { -moz-appearance: textfield; }
                @media (max-width: 600px) {
                    .responsive-promotion-grid { grid-template-columns: 1fr !important; }
                    .form-card-main { padding: 1.25rem !important; }
                    .type-button-row { flex-wrap: wrap !important; }
                    .type-button-row button { flex: none !important; width: calc(50% - 4px) !important; font-size: 0.75rem !important; padding: 10px 4px !important; }
                    .day-btn { min-width: 42px !important; padding: 8px 6px !important; font-size: 0.65rem !important; }
                    .bundle-flow { flex-direction: column !important; }
                    .bundle-flow .arrow-icon { transform: rotate(90deg); }
                }
            `}</style>
            <div
                className="card custom-scrollbar form-card-main"
                onClick={e => e.stopPropagation()}
                style={{ width: '100%', maxWidth: '800px', background: 'var(--bg-sidebar)', maxHeight: '90vh', overflowY: 'auto', padding: '2.5rem', borderRadius: '24px', border: '1px solid var(--border)', boxShadow: '0 30px 60px rgba(0,0,0,0.2)' }}
            >
                {/* Header */}
                <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>
                            {isEdit ? 'Kuponu Düzenle' : 'Kupon Oluştur'}
                        </h2>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Müşterilerin ödeme sırasında girebileceği kupon kodunu tanımlayın.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', padding: '10px',
                            borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--text-main)', transition: 'all 0.2s', outline: 'none'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                    >
                        <X size={20} strokeWidth={2.5} />
                    </button>
                </div>

                <form onSubmit={onSave} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                    {/* Campaign Type Tabs */}
                    <div style={{ display: 'flex', background: 'var(--bg-main)', padding: '4px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        {[
                            { id: 'simple', label: 'Basit Kuponlar', icon: Percent, desc: 'Yüzde veya sabit tutar indirimi' },
                            { id: 'bundle', label: 'Kombine İndirim Kuponu', icon: Package, desc: 'X al Y kazan tarzı kampanyalar' }
                        ].map(tab => (
                            <button key={tab.id} type="button"
                                onClick={() => set('campaignType', tab.id)}
                                style={{
                                    flex: 1, padding: '12px', borderRadius: '10px', fontSize: '0.875rem', fontWeight: 700,
                                    background: (form.campaignType === tab.id || (tab.id === 'simple' && form.campaignType !== 'bundle')) ? 'white' : 'transparent',
                                    color: (form.campaignType === tab.id || (tab.id === 'simple' && form.campaignType !== 'bundle')) ? 'var(--primary)' : 'var(--text-muted)',
                                    boxShadow: (form.campaignType === tab.id || (tab.id === 'simple' && form.campaignType !== 'bundle')) ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                                    transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                                }}>
                                <tab.icon size={16} /> {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Title & Code */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                            Kupon Başlığı *
                        </label>
                        <input required placeholder="Örn: Yaz Kampanyası" value={form.title} onChange={e => set('title', e.target.value)}
                            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '0.9rem', fontWeight: 600 }} />
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                            Kupon Kodu *
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                required
                                value={form.code}
                                onChange={e => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                                placeholder="ör. YAZAR20"
                                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.08em', color: 'var(--primary)', flex: 1 }}
                            />
                            <button
                                type="button"
                                onClick={() => set('code', generateCode())}
                                style={{ padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)', background: 'white', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '0.8125rem', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
                                onMouseOver={e => e.currentTarget.style.color = 'var(--primary)'}
                                onMouseOut={e => e.currentTarget.style.color = 'var(--text-muted)'}
                            >
                                <RefreshCw size={14} /> Oluştur
                            </button>
                        </div>
                    </div>

                    {/* Preview Banner */}
                    {getPreviewText() && (
                        <div style={{
                            padding: '0.875rem 1.25rem', borderRadius: '12px',
                            background: 'rgba(var(--primary-rgb), 0.06)',
                            border: '1px solid rgba(var(--primary-rgb), 0.15)',
                            display: 'flex', alignItems: 'center', gap: '10px',
                            fontSize: '0.8125rem', color: 'var(--primary)', fontWeight: 600
                        }}>
                            <Zap size={16} />
                            <span>Önizleme: {getPreviewText()}</span>
                        </div>
                    )}

                    {/* === SECTION 1: Coupon Value / Bundle === */}
                    {form.campaignType !== 'bundle' ? (
                        <SectionCard>
                            <SectionHeader icon={Percent} title="Kupon Değeri" subtitle="İndirim tipini ve değerini belirleyin" />

                            <div className="type-button-row" style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem' }}>
                                {[
                                    { id: 'percentage', label: t('discounts.typePercentage'), icon: Percent },
                                    { id: 'fixed', label: t('discounts.typeFixed'), icon: Tag }
                                ].map(type => (
                                    <button key={type.id} type="button" onClick={() => set('type', type.id)}
                                        style={{
                                            flex: 1, padding: '14px 12px', borderRadius: '12px',
                                            border: form.type === type.id ? '2px solid var(--primary)' : '1px solid var(--border)',
                                            background: form.type === type.id ? 'rgba(var(--primary-rgb), 0.06)' : 'white',
                                            color: form.type === type.id ? 'var(--primary)' : 'var(--text-main)',
                                            fontSize: '0.8125rem', fontWeight: 700, display: 'flex', flexDirection: 'column',
                                            alignItems: 'center', gap: '6px', transition: 'all 0.2s', cursor: 'pointer'
                                        }}>
                                        <type.icon size={18} />
                                        {type.label}
                                    </button>
                                ))}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="responsive-promotion-grid">
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                                        {form.type === 'percentage' ? 'Yüzde (%) *' : 'Tutar (TL) *'}
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            required type="number" min="0"
                                            max={form.type === 'percentage' ? 100 : undefined}
                                            value={form.value}
                                            onChange={e => set('value', e.target.value)}
                                            placeholder={form.type === 'percentage' ? '20' : '50'}
                                            style={{ paddingRight: '42px', padding: '12px', width: '100%', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '0.9rem', fontWeight: 700 }}
                                        />
                                        <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary)', fontWeight: 800, fontSize: '0.875rem' }}>
                                            {form.type === 'percentage' ? '%' : 'TL'}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('discounts.minAmount')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <input type="number" placeholder="Opsiyonel" value={form.minAmount} onChange={e => set('minAmount', e.target.value)}
                                            style={{ paddingRight: '42px', padding: '12px', width: '100%', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '0.9rem' }} />
                                        <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.875rem' }}>TL</div>
                                    </div>
                                    <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>Boş bırakılırsa minimum tutar şartı olmaz</p>
                                </div>
                            </div>
                        </SectionCard>
                    ) : (
                        <SectionCard>
                            <SectionHeader icon={Package} title="Kombine Kupon" subtitle="'X al Y kazan' tarzı kampanya oluşturun" color="#f59e0b" />

                            <div className="bundle-flow" style={{ display: 'flex', gap: '1rem', alignItems: 'stretch' }}>
                                {/* Buy Side */}
                                <div style={{ flex: 1, padding: '1.25rem', background: 'white', borderRadius: '14px', border: '2px solid rgba(var(--primary-rgb), 0.15)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800 }}>1</div>
                                        <span style={{ fontWeight: 800, fontSize: '0.875rem', color: 'var(--text-main)' }}>Şartlar (Ne Alınacak?)</span>
                                    </div>
                                    <div style={{ marginBottom: '1rem' }}>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Miktar *</label>
                                        <input type="number" min="1" value={form.buyQuantity} onChange={e => set('buyQuantity', e.target.value)}
                                            style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid var(--border)', fontWeight: 700, fontSize: '0.9rem' }} />
                                    </div>
                                    <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                        <input placeholder="Ürün ara..." value={productSearchQuery} onChange={e => setProductSearchQuery(e.target.value)}
                                            style={{ padding: '10px 12px 10px 36px', width: '100%', borderRadius: '10px', border: '1px solid var(--border)' }} />
                                    </div>
                                    <ProductSelector menuData={menuData} selectedList={form.buyItems} onUpdate={newItems => set('buyItems', newItems)} query={productSearchQuery} t={t} />
                                    {form.buyItems?.length > 0 && (
                                        <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 700 }}>
                                            {form.buyItems.length} ürün seçili
                                        </div>
                                    )}
                                </div>

                                {/* Arrow */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.5rem' }}>
                                    <div className="arrow-icon" style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <ArrowRight size={20} />
                                    </div>
                                </div>

                                {/* Get Side */}
                                <div style={{ flex: 1, padding: '1.25rem', background: 'white', borderRadius: '14px', border: '2px solid rgba(245, 158, 11, 0.2)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#f59e0b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800 }}>2</div>
                                            <span style={{ fontWeight: 800, fontSize: '0.875rem', color: 'var(--text-main)' }}>Fırsat (Ne Verilecek?)</span>
                                        </div>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={form.sameItemMode} onChange={e => set('sameItemMode', e.target.checked)} style={{ accentColor: 'var(--primary)' }} />
                                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>Aynı ürünler</span>
                                        </label>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                                        <div>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Miktar</label>
                                            <input type="number" min="1" value={form.getQuantity} onChange={e => set('getQuantity', e.target.value)}
                                                style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid var(--border)', fontWeight: 700, fontSize: '0.9rem' }} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>İndirim (%)</label>
                                            <div style={{ position: 'relative' }}>
                                                <input type="number" min="0" max="100" value={form.bundleDiscountValue} onChange={e => set('bundleDiscountValue', e.target.value)}
                                                    placeholder="100" style={{ width: '100%', padding: '10px', paddingRight: '36px', borderRadius: '10px', border: '1px solid var(--border)', fontWeight: 700, fontSize: '0.9rem' }} />
                                                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontWeight: 800, color: '#f59e0b' }}>%</span>
                                            </div>
                                            {(form.bundleDiscountValue === '100' || form.bundleDiscountValue === 100) && (
                                                <span style={{ fontSize: '0.65rem', color: '#f59e0b', fontWeight: 700 }}>= Bedava</span>
                                            )}
                                        </div>
                                    </div>
                                    {!form.sameItemMode && (
                                        <>
                                            <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                                                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                                <input placeholder="Hedef ürün ara..." value={getSearchQuery} onChange={e => setGetSearchQuery(e.target.value)}
                                                    style={{ padding: '10px 12px 10px 36px', width: '100%', borderRadius: '10px', border: '1px solid var(--border)' }} />
                                            </div>
                                            <ProductSelector menuData={menuData} selectedList={form.getItems} onUpdate={newItems => set('getItems', newItems)} query={getSearchQuery} t={t} />
                                        </>
                                    )}
                                </div>
                            </div>
                        </SectionCard>
                    )}

                    {/* === SECTION 2: Target & Scope === */}
                    <SectionCard>
                        <SectionHeader icon={Users} title="Hedef Kitle & Kapsam" subtitle="Bu kuponun kimlere ve hangi ürünlere uygulanacağını belirleyin" color="#3b82f6" />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="responsive-promotion-grid">
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)' }}>{t('discounts.target')}</label>
                                <select value={form.target} onChange={e => set('target', e.target.value)}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '0.875rem', fontWeight: 600, background: 'white' }}>
                                    <option value="all">{t('discounts.targetAll')} ({customers?.length || 0})</option>
                                    <option value="groups">{t('discounts.targetGroups', 'Seviyeler / Roller')}</option>
                                    <option value="customers">{t('discounts.targetSpecific')} {form.selectedCustomers?.length > 0 ? `(${form.selectedCustomers.length} Seçili)` : ''}</option>
                                </select>
                            </div>
                            {form.campaignType !== 'bundle' && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)' }}>{t('discounts.scope')}</label>
                                    <select value={form.productScope} onChange={e => set('productScope', e.target.value)}
                                        style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '0.875rem', fontWeight: 600, background: 'white' }}>
                                        <option value="all">{t('discounts.scopeAll', 'Tüm Ürünler')}</option>
                                        <option value="specific_products">{t('discounts.scopeSpecific', 'Sadece Seçili Ürünler')}</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        {form.target === 'groups' && (() => {
                            const levels = tenantData?.loyaltyLevels || tenantData?.loyalty_levels || defaultLoyaltyLevels;
                            const roles = tenantData?.visitRoles || tenantData?.visit_roles || defaultVisitRoles;
                            return (
                                <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="responsive-promotion-grid">
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)' }}>Sadakat Seviyesi</label>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                            {levels.map(level => {
                                                const selected = (form.loyaltyStatus || []).includes(level.label);
                                                return (
                                                    <button key={level.id || level.label} type="button"
                                                        onClick={() => {
                                                            const current = form.loyaltyStatus || [];
                                                            set('loyaltyStatus', selected ? current.filter(l => l !== level.label) : [...current, level.label]);
                                                        }}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                                                            border: selected ? `2px solid ${level.color}` : '1px solid var(--border)',
                                                            background: selected ? level.color + '18' : 'white',
                                                            color: selected ? level.color : 'var(--text-muted)',
                                                        }}
                                                    >
                                                        {level.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)' }}>Ziyaret Rolü</label>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                            {roles.map(role => {
                                                const selected = (form.visitRole || []).includes(role.label);
                                                return (
                                                    <button key={role.id || role.label} type="button"
                                                        onClick={() => {
                                                            const current = form.visitRole || [];
                                                            set('visitRole', selected ? current.filter(r => r !== role.label) : [...current, role.label]);
                                                        }}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                                                            border: selected ? `2px solid ${role.color}` : '1px solid var(--border)',
                                                            background: selected ? role.color + '18' : 'white',
                                                            color: selected ? role.color : 'var(--text-muted)',
                                                        }}
                                                    >
                                                        {role.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {form.campaignType !== 'bundle' && form.productScope === 'specific_products' && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: 'white', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                    <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-main)' }}>Ürün Seçimi</span>
                                    {(form.selectedProducts || []).length > 0 && (
                                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)', background: 'rgba(var(--primary-rgb), 0.08)', padding: '3px 10px', borderRadius: '6px' }}>
                                            {form.selectedProducts.length} seçili
                                        </span>
                                    )}
                                </div>
                                <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                    <input placeholder="Ürün ara..." value={productSearchQuery} onChange={e => setProductSearchQuery(e.target.value)}
                                        style={{ padding: '10px 12px 10px 38px', width: '100%', borderRadius: '10px', border: '1px solid var(--border)' }} />
                                </div>
                                <ProductSelector menuData={menuData} selectedList={form.selectedProducts || []} onUpdate={newItems => set('selectedProducts', newItems)} query={productSearchQuery} t={t} />
                            </div>
                        )}
                    </SectionCard>

                    {/* === SECTION 3: Schedule === */}
                    <SectionCard>
                        <SectionHeader icon={Calendar} title="Zamanlama" subtitle="Kuponun geçerli olduğu gün, tarih ve saat aralığı" color="#10b981" />

                        {/* Day Selection */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)' }}>Geçerli Günler</label>
                                <button type="button" onClick={() => set('applicableDays', allDaysSelected ? [] : [0, 1, 2, 3, 4, 5, 6])}
                                    style={{ fontSize: '0.7rem', color: 'var(--primary)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>
                                    {allDaysSelected ? 'Hiçbirini Seçme' : 'Tümünü Seç'}
                                </button>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {dayNames.map((day, index) => {
                                    const isSelected = (form.applicableDays || [0, 1, 2, 3, 4, 5, 6]).includes(index);
                                    return (
                                        <button key={index} type="button" className="day-btn"
                                            onClick={() => {
                                                const currentDays = form.applicableDays || [0, 1, 2, 3, 4, 5, 6];
                                                const newDays = isSelected ? currentDays.filter(d => d !== index) : [...currentDays, index];
                                                set('applicableDays', newDays);
                                            }}
                                            style={{
                                                padding: '8px 14px', borderRadius: '10px',
                                                border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border)',
                                                background: isSelected ? 'rgba(var(--primary-rgb), 0.08)' : 'white',
                                                color: isSelected ? 'var(--primary)' : 'var(--text-muted)',
                                                fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', minWidth: '70px'
                                            }}>
                                            {day}
                                        </button>
                                    );
                                })}
                            </div>
                            {(!form.applicableDays || form.applicableDays.length === 0) && (
                                <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Info size={12} /> En az bir gün seçmelisiniz
                                </p>
                            )}
                        </div>

                        {/* Date Range */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }} className="responsive-promotion-grid">
                            <div>
                                <label style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '0.5rem', display: 'block' }}>Başlangıç Tarihi</label>
                                <PremiumDatePicker value={form.startDate} onChange={date => set('startDate', date)} />
                            </div>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 700 }}>Bitiş Tarihi</label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={isIndefinite}
                                            onChange={e => { setIsIndefinite(e.target.checked); if (e.target.checked) set('expiryDate', null); }}
                                            style={{ accentColor: 'var(--primary)' }} />
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Süresiz</span>
                                    </label>
                                </div>
                                {!isIndefinite ? (
                                    <PremiumDatePicker
                                        value={form.expiryDate ? new Date(form.expiryDate) : null}
                                        onChange={date => { set('expiryDate', date ? date.toISOString().split('T')[0] : null); setIsIndefinite(false); }}
                                        minDate={form.startDate || new Date()}
                                    />
                                ) : (
                                    <div style={{ height: '45px', display: 'flex', alignItems: 'center', padding: '0 12px', background: 'white', borderRadius: '12px', border: '1px dashed var(--border)', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                                        Bitiş tarihi yok (süresiz)
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Time Range */}
                        <div style={{ padding: '1rem', background: 'white', borderRadius: '12px', border: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (form.startTime === '00:00' && form.expiryTime === '23:59') ? 0 : '0.75rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Clock size={14} style={{ color: 'var(--text-muted)' }} />
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-main)' }}>Günlük Aktif Saat Aralığı</label>
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={form.startTime === '00:00' && form.expiryTime === '23:59'}
                                        onChange={e => {
                                            if (e.target.checked) { set('startTime', '00:00'); set('expiryTime', '23:59'); }
                                            else { set('startTime', '07:00'); set('expiryTime', '23:00'); }
                                        }}
                                        style={{ accentColor: 'var(--primary)' }} />
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Tüm Gün</span>
                                </label>
                            </div>
                            {!(form.startTime === '00:00' && form.expiryTime === '23:59') && (
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                    <div style={{ flex: 1 }}>
                                        <input type="time" value={form.startTime || '07:00'} onChange={e => set('startTime', e.target.value)}
                                            style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid var(--border)', height: '44px', fontSize: '0.875rem', fontWeight: 600 }} />
                                    </div>
                                    <span style={{ fontSize: '1.25rem', color: 'var(--text-muted)' }}>→</span>
                                    <div style={{ flex: 1 }}>
                                        <input type="time" value={form.expiryTime || '23:00'} onChange={e => set('expiryTime', e.target.value)}
                                            style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid var(--border)', height: '44px', fontSize: '0.875rem', fontWeight: 600 }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </SectionCard>

                    {/* === SECTION 4: Limits === */}
                    <SectionCard>
                        <SectionHeader icon={Shield} title="Kullanım Limitleri" subtitle="Kuponun kaç kez kullanılabileceğini sınırlayın" color="#8b5cf6" />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="responsive-promotion-grid">
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)' }}>Toplam Limit</label>
                                <input type="number" placeholder="∞ Sınırsız" value={form.totalLimit} onChange={e => set('totalLimit', e.target.value)}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '0.9rem' }} />
                                <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>Tüm müşterilerin toplam kullanımı</p>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)' }}>Kişi Başı Limit</label>
                                <input type="number" placeholder="∞ Sınırsız" value={form.customerLimit} onChange={e => set('customerLimit', e.target.value)}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', fontSize: '0.9rem' }} />
                                <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>Her müşteri en fazla kaç kez kullanabilir</p>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Active Toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: 'var(--bg-main)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                        <div>
                            <div style={{ fontWeight: 700, marginBottom: '2px', fontSize: '1rem', color: 'var(--text-main)' }}>Kupon Aktif</div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Pasif kuponlar müşteriler tarafından kullanılamaz</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => set('isActive', !form.isActive)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: form.isActive ? 'var(--primary)' : 'var(--text-muted)', padding: '4px' }}
                        >
                            {form.isActive ? <ToggleRight size={44} /> : <ToggleLeft size={44} />}
                        </button>
                    </div>

                    {/* Submit */}
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)' }}>
                        <button type="button" onClick={onClose} style={{
                            flex: 1, padding: '14px', background: 'transparent', color: 'var(--text-main)',
                            border: '1px solid var(--border)', borderRadius: '12px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer'
                        }}>İptal</button>
                        <button type="submit" className="btn-primary" disabled={saving} style={{
                            flex: 1.5, fontWeight: 700, padding: '14px', borderRadius: '12px', border: 'none', fontSize: '0.9rem',
                            opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                        }}>
                            {saving ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Kupon Oluştur'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


export default Coupons;
